import type {User} from 'oidc-client-ts'
import {ErrorResponse, UserManager, WebStorageStateStore} from 'oidc-client-ts'

export const isKeycloakConfigured = Boolean(process.env.KEYCLOAK_ISSUER_URL && process.env.KEYCLOAK_CLIENT_ID)

/**
 * Alias of the identity provider the realm federates the staff through, when
 * the deployment has one. Sending it walks past the identity-first page the
 * realm installs with organizations, so a sign-in lands on the provider.
 *
 * Absent locally, where the realm federates no one. Keycloak ignores a hint
 * whose alias it does not know and serves its own credentials form instead, so
 * a stale value costs a redirect, it does not break the sign-in.
 */
export const idpHint: string | undefined = process.env.KEYCLOAK_IDP_HINT

/**
 * Where Keycloak sends the browser back after a sign-out.
 *
 * A page of its own, not the origin, and the one page the automatic sign-in
 * never fires on. The provider keeps a session outliving Keycloak's: coming
 * back to any other path would sign the user straight back in, which is a
 * sign-out that does not sign out.
 *
 * ⚠️ Enumerated in the realm (`valid_post_logout_redirect_uris`) without a
 * wildcard: changing this path means changing it there too, or Keycloak
 * refuses the redirect and strands the user on its own error page.
 */
export const SIGNED_OUT_PATH = '/auth/signed_out'

/**
 * Names a session nothing can revive: the token endpoint refusing the grant.
 * A network error or a 5xx is not one — it must surface instead of passing for
 * a signed-out user, or an identity-provider outage would send every request
 * out anonymous and bounce the user to /logout.
 */
export const isDeadSessionError = (err: unknown): boolean =>
  err instanceof ErrorResponse && (err.error === 'invalid_grant' || err.error === 'invalid_token')

let userManager: UserManager
let renewal: Promise<User | null> | undefined
let renewalDead = false
const renewalDeadListeners = new Set<(dead: boolean) => void>()

const setRenewalDead = (dead: boolean): void => {
  if (renewalDead === dead) {
    return
  }
  renewalDead = dead
  for (const listener of renewalDeadListeners) {
    listener(dead)
  }
}

/** True once a renewal has failed for good: a session held but unusable. */
export const isRenewalDead = (): boolean => renewalDead

export const onRenewalDeadChange = (listener: (dead: boolean) => void): (() => void) => {
  renewalDeadListeners.add(listener)
  return () => {
    renewalDeadListeners.delete(listener)
  }
}

/**
 * The one UserManager. A single instance matters: React reads the session
 * through it (react-oidc-context) and the token getter reads it outside React,
 * so two instances would hold two divergent sessions.
 *
 * Browser only — it reads window for the redirect uri and the stores.
 */
export const getKeycloakUserManager = (): UserManager => {
  if (!userManager) {
    userManager = new UserManager({
      authority: process.env.KEYCLOAK_ISSUER_URL,
      client_id: process.env.KEYCLOAK_CLIENT_ID,
      // The realm enumerates its redirect URIs without wildcards, so this must
      // stay the bare origin: the page to return to travels in the OIDC state,
      // never in the redirect_uri.
      redirect_uri: window.location.origin,
      post_logout_redirect_uri: `${window.location.origin}${SIGNED_OUT_PATH}`,
      scope: 'openid email profile',
      // localStorage so every tab shares the session and the token getter can
      // run outside React — the Apollo client lives outside the tree.
      userStore: new WebStorageStateStore({store: window.localStorage}),
      // sessionStorage where the library defaults to localStorage: a pending
      // login belongs to the tab that started it, and dies with it rather than
      // outliving a browser restart.
      stateStore: new WebStorageStateStore({store: window.sessionStorage}),
      // Renewal is driven below instead: the library's SilentRenewService
      // calls signinSilent() straight on the manager, outside the single-flight
      // guard, and two concurrent grants on one refresh token drop the whole
      // session as soon as the realm rotates them.
      automaticSilentRenew: false,
    })

    userManager.events.addAccessTokenExpiring(() => {
      void renewKeycloakUser().then(
        user => setRenewalDead(!user),
        err => setRenewalDead(isDeadSessionError(err)),
      )
    })
    // Subscribed at construction rather than from a React effect: a failure in
    // the gap before an effect runs would leave the tab reporting a session it
    // has no usable token for, until a manual reload.
    userManager.events.addUserLoaded(() => setRenewalDead(false))
  }

  return userManager
}

const RENEW_LOCK = 'ambler:keycloak-renew'

/**
 * Renews once for the whole browser, not once per tab.
 *
 * The refresh token is shared through localStorage but each tab runs its own
 * module, so the in-tab guard below does not span them: four back-office tabs
 * waking from sleep would send four grants carrying the same refresh token,
 * and the realm revokes the session past `refresh_token_max_reuse`. The
 * previous Auth0 client serialized this with a cross-tab lock; oidc-client-ts
 * has none, so it is on us.
 *
 * Re-reading the user once the lock is held is the point: whoever waited
 * usually finds the session already renewed by the tab ahead, and has nothing
 * left to do.
 *
 * Where the Web Locks API is missing the callback simply runs unserialized —
 * every other guarantee still holds, only the cross-tab race stays open.
 */
const renewUnderLock = (manager: UserManager): Promise<User | null> => {
  const renew = async (): Promise<User | null> => {
    const current = await manager.getUser()
    if (current && !current.expired) {
      return current
    }
    if (!current?.refresh_token) {
      return null
    }

    return manager.signinSilent()
  }

  return navigator.locks ? navigator.locks.request(RENEW_LOCK, renew) : renew()
}

/**
 * In-app renewal — refresh-token grant only, one at a time.
 *
 * Never falls into the iframe leg: silent_redirect_uri defaults to our own
 * origin, which is served with X-Frame-Options: DENY, so each attempt would
 * hang for the navigator's 10s timeout before failing. The shared promise
 * keeps one call per tab (oidc-client-ts has no in-flight guard of its own,
 * and Apollo fires requests in bursts); the lock above keeps one across them.
 */
export const renewKeycloakUser = async (): Promise<User | null> => {
  // Before anything else: a renewal already in flight here is the answer, and
  // it spares both the store read and the lock round-trip.
  if (renewal) {
    return renewal
  }

  renewal = renewUnderLock(getKeycloakUserManager()).finally(() => {
    renewal = undefined
  })

  return renewal
}
